import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, FieldLabel, SelectField, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { Icon } from "../base/Icon";
import { InputField } from "../base/InputField";
import { VoicePreview } from "./VoicePreview";
import { useRecording } from "../../hooks/useRecording";

const VOICE_LANGUAGES = [
  { label: "Chinese", value: "zh" },
  { label: "Dutch", value: "nl" },
  { label: "English", value: "en" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
  { label: "Hindi", value: "hi" },
  { label: "Italian", value: "it" },
  { label: "Japanese", value: "ja" },
  { label: "Korean", value: "ko" },
  { label: "Polish", value: "pl" },
  { label: "Portuguese", value: "pt" },
  { label: "Russian", value: "ru" },
  { label: "Spanish", value: "es" },
  { label: "Swedish", value: "sv" },
  { label: "Turkish", value: "tr" },
];

const DIALOG_SAMPLE_KEY = "create-voice-sample";
const VOICE_CLONE_TIPS = [
  "Keep it short. Aim for around 10 seconds—the sweet spot for accurate, high-similarity voice cloning. Longer recordings don’t improve results.",
  "Pick the right script. Choose text that matches the tone and style you want in the generated voice. For example, don’t use a flat transcript if you’re aiming for an expressive clone—prepare a script with the right energy for your use case.",
  "Record with clarity. Speak as clearly as possible and minimize background noise. A good microphone in a quiet space makes a big difference.",
  "Keep the pacing natural. Avoid long pauses, since they can cause the cloned voice to drift from the source.",
  "Use the target language. Record in the same language you want the clone to speak (e.g., record in Spanish if you want a Spanish-speaking clone).",
];

const VoiceCloneTips = ({ className = "" }: { className?: string }) => {
  const baseClasses =
    "max-h-32 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-left text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-200";

  return (
    <View className={`${baseClasses}${className ? ` ${className}` : ""}`}>
      <Text className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Voice cloning tips</Text>
      <View className="space-y-2">
        {VOICE_CLONE_TIPS.map((tip) => (
          <View
            key={tip}
            className="flex items-start gap-2"
          >
            <Text
              aria-hidden
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              •
            </Text>
            <Text className="leading-relaxed text-gray-700 dark:text-gray-100">{tip}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

type AudioSamplesController = {
  playSample: (key: string, audio: HTMLAudioElement) => void;
  stopSample: (key?: string) => void;
  activeSampleKey: string | null;
};

type CreateVoiceCloneDialogueProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateVoice: (params: { name: string; description: string; language: string; file: File }) => Promise<void>;
  audioSamples: AudioSamplesController;
};

export const CreateVoiceCloneDialogue = ({ isOpen, onClose, onCreateVoice, audioSamples }: CreateVoiceCloneDialogueProps) => {
  const [voiceName, setVoiceName] = useState("");
  const [voiceDescription, setVoiceDescription] = useState("");
  const [voiceLanguage, setVoiceLanguage] = useState("en");
  const [recordingMode, setRecordingMode] = useState<"upload" | "record">("upload");
  const [voiceSampleFile, setVoiceSampleFile] = useState<File | null>(null);
  const [voiceSampleUrl, setVoiceSampleUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { playSample, stopSample, activeSampleKey } = audioSamples;
  const { start: startRecording, stop: stopRecording, isLoadingRecording, isRecording } = useRecording();

  const isSampleActive = activeSampleKey === DIALOG_SAMPLE_KEY;

  const updateVoiceSample = useCallback((file: File | null) => {
    setVoiceSampleFile(file);
    setVoiceSampleUrl((previousUrl) => {
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      return file ? URL.createObjectURL(file) : null;
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClearVoiceSample = useCallback(() => {
    updateVoiceSample(null);
    stopSample(DIALOG_SAMPLE_KEY);
  }, [stopSample, updateVoiceSample]);

  const resetForm = useCallback(() => {
    setVoiceName("");
    setVoiceDescription("");
    setVoiceLanguage("en");
    setRecordingMode("upload");
    setIsSubmitting(false);
    handleClearVoiceSample();
  }, [handleClearVoiceSample]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      if (isRecording) {
        void stopRecording();
      }
    }
  }, [isOpen, isRecording, resetForm, stopRecording]);

  useEffect(() => {
    return () => {
      if (voiceSampleUrl) {
        URL.revokeObjectURL(voiceSampleUrl);
      }
    };
  }, [voiceSampleUrl]);

  const handleFileUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      if (file) {
        updateVoiceSample(file);
      }
    },
    [updateVoiceSample],
  );

  const handleStartRecording = useCallback(() => {
    void startRecording();
  }, [startRecording]);

  const handleStopRecording = useCallback(async () => {
    try {
      const file = await stopRecording();
      if (file) {
        updateVoiceSample(file);
      } else {
        alert("Recording failed. Please try again.");
      }
    } catch (error) {
      alert("Error capturing recording. Please try again.");
    }
  }, [stopRecording, updateVoiceSample]);

  const ensureRecordedFile = useCallback(async (): Promise<File | null> => {
    if (isRecording) {
      return stopRecording();
    }
    return voiceSampleFile;
  }, [isRecording, stopRecording, voiceSampleFile]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = voiceName.trim();
    if (!trimmedName) {
      return;
    }

    let sampleToUpload: File | null = voiceSampleFile;

    if (recordingMode === "record") {
      const recordedFile = await ensureRecordedFile();
      if (recordedFile) {
        sampleToUpload = recordedFile;
        if (recordedFile !== voiceSampleFile) {
          updateVoiceSample(recordedFile);
        }
      } else {
        alert("Please record audio first by clicking 'Start Recording', then 'Stop Recording'");
        return;
      }
    } else if (!voiceSampleFile) {
      alert("Please select an audio file to upload");
      return;
    }

    if (!sampleToUpload) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onCreateVoice({
        name: trimmedName,
        description: voiceDescription.trim(),
        language: voiceLanguage,
        file: sampleToUpload,
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error("Error creating voice:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [ensureRecordedFile, onCreateVoice, onClose, recordingMode, resetForm, updateVoiceSample, voiceDescription, voiceLanguage, voiceName, voiceSampleFile]);

  const handleDialogClose = useCallback(() => {
    if (isRecording) {
      void stopRecording();
    }
    onClose();
  }, [isRecording, onClose, stopRecording]);

  const submitDisabled = useMemo(() => {
    if (isSubmitting || isRecording || isLoadingRecording) {
      return true;
    }
    if (!voiceName.trim()) {
      return true;
    }
    if (recordingMode === "upload") {
      return !voiceSampleFile;
    }
    return false;
  }, [isLoadingRecording, isRecording, isSubmitting, recordingMode, voiceName, voiceSampleFile]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title="Create Custom Voice"
      size="lg"
    >
      <View className="p-4 space-y-4 max-h-[calc(90vh-6rem)] overflow-y-auto">
        <View>
          <InputField
            label="Voice Name"
            value={voiceName}
            onChange={setVoiceName}
            placeholder="Enter voice name..."
            className="w-full"
          />
        </View>

        <View>
          <InputField
            label="Description"
            value={voiceDescription}
            onChange={setVoiceDescription}
            placeholder="Enter voice description..."
            className="w-full"
          />
        </View>

        <View>
          <SelectField
            label="Language"
            value={voiceLanguage}
            onChange={setVoiceLanguage}
            options={VOICE_LANGUAGES}
            className="w-full"
          />
        </View>

        <View>
          <FieldLabel label="Audio Source">
            <View className="flex space-x-2">
              <Button
                onClick={() => setRecordingMode("upload")}
                variant={recordingMode === "upload" ? "brand" : "outline"}
                className="flex-1"
              >
                <Icon
                  name="upload"
                  size={16}
                  className="mr-2"
                />
                Upload File
              </Button>
              <Button
                onClick={() => setRecordingMode("record")}
                variant={recordingMode === "record" ? "brand" : "outline"}
                className="flex-1"
              >
                <Icon
                  name="mic"
                  size={16}
                  className="mr-2"
                />
                Record Audio
              </Button>
            </View>
          </FieldLabel>
        </View>

        {recordingMode === "upload" ? (
          <View>
            <FieldLabel label="Audio File">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <View className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="brand"
                  className="flex-1"
                >
                  <Icon
                    name="upload"
                    size={16}
                    className="mr-2"
                  />
                  <Text>Choose File</Text>
                </Button>
              </View>
              <VoiceCloneTips className="mt-3" />
            </FieldLabel>
          </View>
        ) : (
          <View>
            <FieldLabel label="Record Audio">
              <View className="flex items-center space-x-4">
                {!isRecording ? (
                  <Button
                    onClick={handleStartRecording}
                    variant="brand"
                    isDisabled={isLoadingRecording}
                    className="flex items-center space-x-2"
                  >
                    <Icon
                      name="mic"
                      size={16}
                    />
                    <Text>{isLoadingRecording ? "Starting..." : "Start Recording"}</Text>
                  </Button>
                ) : (
                  <Button
                    onClick={handleStopRecording}
                    variant="outline"
                    className="flex items-center space-x-2 text-red-600 dark:text-red-400"
                  >
                    <Icon
                      name="square"
                      size={16}
                    />
                    <Text>Stop Recording</Text>
                  </Button>
                )}
                {isRecording && (
                  <View className="flex items-center space-x-2">
                    <View className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <Text className="text-sm text-gray-500 dark:text-gray-400">Recording...</Text>
                  </View>
                )}
              </View>
              <VoiceCloneTips className="mt-3" />
            </FieldLabel>
          </View>
        )}

        {voiceSampleFile && voiceSampleUrl && (
          <VoicePreview
            audioUrl={voiceSampleUrl}
            audioKey={DIALOG_SAMPLE_KEY}
            onPlaySample={playSample}
            isActive={isSampleActive}
            filename={voiceSampleFile.name}
            onClose={handleClearVoiceSample}
            showDownload={true}
          />
        )}

        <View className="flex items-center justify-end space-x-2 pt-4">
          <Button
            onClick={handleDialogClose}
            variant="outline"
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="brand"
            isDisabled={submitDisabled}
            className="flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Icon
                  name="loader"
                  size={16}
                  className="animate-spin"
                />
                <Text>Creating Voice...</Text>
              </>
            ) : (
              <Text>Create Voice</Text>
            )}
          </Button>
        </View>
      </View>
    </Dialog>
  );
};
